import { sessionStorage, storage } from "@utils/storage";
import { router } from "@/router";
import { _get, _getNull, debounce, decodeUrl, getRandomId, showToast, sleep, urlParamToJson } from "@utils/utils";
import { accountLogin } from "@server/accountApi";
import pinia from '@/store/pinia';
import { useUserInfoStore } from '@/store/userInfo';
import { getAppClientConfig, getProvider, isWxH5 } from "@utils/business";
import config from "@/config";
import { CONST_KEY } from "@utils/const";
import { infoRedDot } from "@server/infoApi";
import {
    clearOfficialAccountCode, clearUserOfficialAccountCode,
    getOfficialAccountCode,
    isHasOfficialAccountCode,
    isHasUserOfficialAccountCode
} from "@utils/officialAccount";
import { setLoginSuccessRouter } from "@utils/route";
import { onLaunch } from "@dcloudio/uni-app";

// 用户信息
const userInfoStore = useUserInfoStore(pinia);

/**
 * 获取 code
 * @returns {Promise<string>} - 成功返回 code 字符串，失败返回 null
 */
export const getCode = () => {
    return new Promise(async resolve => {
        const provider = await getProvider();
        uni.login({
            provider: provider,
            success: (loginRes) => {
                resolve(_get(loginRes, 'code', null))
            },
            fail: () => {
                resolve(null)
            }
        });
    })
}

/**
 * 获取用户id
 * @returns {*|string|string}
 */
export const getUserId = () => {
    try {
        return userInfoStore.userInfo.userId || ''
    } catch (e) {
        return ''
    }
}

/**
 * 是否登录
 * @returns {*}
 */
export const isLogin = () => {
    try {
        if (isWxH5() && !getUserId()) {
            userInfoStore.setUserInfo({
                accessToken: ''
            })
        }
        return userInfoStore.userInfo.accessToken || ''
    } catch (e) {
        return ''
    }
}

/**
 * 获取token
 * @returns {*}
 */
export const getAccessToken = () => {
    return isLogin();
}

/**
 * 删除token
 * @returns {*}
 */
export const clearUserInfo = () => {
    userInfoStore.setUserInfo({}, true)
    // clearUserHandLoginOut();
}

/**
 * 重定向到登录页
 * @returns {boolean} - true,需要跳转到登录页 false，不需要跳转到登录页及有登录态
 */
export const redirect2Login = (routerInfo = null) => {
    if (isLogin()) {
        return false;
    }

    if (routerInfo) {
        setLoginSuccessRouter(routerInfo)
    }

    // #ifdef MP-WEIXIN
    router.navigateTo({
        name: 'login'
    })
    // #endif

    // #ifdef H5
    router.navigateTo({
        name: 'loginPhone'
    })
    // #endif

    return true;
}

/**
 * 登录过期后的逻辑，同步逻辑
 */
export const login401Sync = () => {
    clearUserInfo();
    router.reLaunch({ name: 'home' })
}

/**
 * 自动登录
 * @returns {Promise<void>}
 */
export const autoLogin = async (isGetCode = true) => {
    // 用户已经登录 或者 用户手动退出
    if (isLogin() || isUserHandLoginOut()) return

    // #ifdef H5
    if (!isHasOfficialAccountCode() && isGetCode) {
        getOfficialAccountCode({
            redirect: `${config.h5DirectUrl}/wxapp/?__t=${+new Date()}#/pages/common/h5-transit/index`,
        })
        return
    }
    // #endif

    const res = await accountLogin({
        // #ifdef MP-WEIXIN
        wxMiniCode: await getCode(),
        wxMiniAppId: config.appid,
        // #endif

        // #ifdef H5
        wxMpCode: isHasOfficialAccountCode() || isHasUserOfficialAccountCode(),
        wxMpAppId: config.appid,
        // #endif
    });

    if (!res.$success) return;
    clearOfficialAccountCode();
    clearUserOfficialAccountCode();
    userInfoStore.setUserInfo(res.data)
};

/**
 * 登录过期后的逻辑，异步逻辑
 */
export const login401Async = debounce(async () => {
    showToast("登录失效，请重新登录~")
    await sleep(2000);
    login401Sync();
}, 1000);


/**
 * 清除用户手动退出记录
 */
export const clearUserHandLoginOut = () => {
    storage.remove(CONST_KEY.userLoginOut);
}

/**
 * 用户手动退出登录  或者 手动注销，在缓存内写入标，用来禁止自动登录，只能手动登录
 */
export const userHandLogOut = () => {
    clearUserInfo()
    storage.set(CONST_KEY.userLoginOut, 1);
}

/**
 * 判断是否用户手动退出
 * @returns {*}
 */
export const isUserHandLoginOut = () => {
    return storage.get(CONST_KEY.userLoginOut);
}

// let _refreshLoginToken = null;
//
// export const refreshLoginToken = () => {
//     // 如果本地有值，直接去本地值
//     if (getToken()) {
//         _refreshLoginToken = null;
//         return 'token'
//     }
//     if (_refreshLoginToken) return _refreshLoginToken;
//
//     _refreshLoginToken = new Promise(resolve => {
//         // 请求token
//         const res = {};
//         // 做一些缓存等逻辑
//         // ...
//         // 返回 token
//         return res.token;
//     })
//     return _refreshLoginToken;
// }
//
// // 外部调用，当已知登录过期，需要重新登录
// // 拿到token
// let token = await refreshLoginToken();
// if (!token) {
//     // 登录异常
// }
// // 否则登录成功，重新发送请求


/**
 * 解析小程序码参数
 * @returns {{}|{}|*}
 * @param scene
 */
export const getScene = (scene) => {
    try {
        return urlParamToJson(decodeUrl(scene))
    } catch (e) {
        return {}
    }
}

/**
 * 初始化邀请赚钱信息，在新用户注册时需要上报改字段
 * @param options
 */
export const initInviteInfo = (options) => {
    try {
        // 左面解析小程序码 右面 解析转发卡片
        const inviteUserId = _getNull(getScene(_getNull(options, 'query.scene')), 'inviteUserId') || _getNull(options, 'query.inviteUserId');
        inviteUserId && storage.set('inviteUserId', inviteUserId)
    } catch (e) {
        console.error(e)
    }
}

/**
 * 删除邀请赚钱信息
 */
export const clearInviteInfo = () => {
    storage.remove('inviteUserId')
}

/**
 * 初始化唯一id
 */
export const initOnlyId = () => {
    try {
        uni.getStorage({
            key: 'onlyId',
            // 没查到key
            fail: () => {
                storage.set('onlyId', getRandomId())
            }
        })
    } catch (e) {
        console.error(e)
    }
}

/**
 * 获取用户唯一id
 * @returns {*}
 */
export const getOnlyId = () => {
    return storage.get('onlyId')
}

/**
 * 全局小红点
 */
export const initRedDot = () => {
    const token = isLogin();
    if (!token) return
    infoRedDot({}, { accesstoken: token, }).then(res => {
        if (!res.$success) return;
        userInfoStore.setUserInfo({
            newOrderNum: res.data.newOrderNum > 99 ? '99+' : res.data.newOrderNum,
            newNoticeNum: res.data.newNoticeNum > 99 ? '99+' : res.data.newNoticeNum,
        })
    })
}

export const isGirls = () => {
    if (!isWxH5()) {
        return getAppClientConfig('sex') === 2
    }
    try {
        return userInfoStore.userInfo.sex === 2
    } catch (e) {
        return null;
    }
}

/**
 * 申请助理中
 * @returns {null|boolean}
 */
export const isApplyIngWaiter = () => {
    try {
        return userInfoStore.userInfo.waiterStatus === 1
    } catch (e) {
        return null;
    }
}

/**
 * 是否助理
 * @returns {null|boolean}
 */
export const isWaiter = () => {
    try {
        return userInfoStore.userInfo.waiterStatus === 2
    } catch (e) {
        return null;
    }
}

export const getClient = () => {
    try {
        return userInfoStore.userInfo.client || ''
    } catch (e) {
        return '';
    }
}

export const isBindOpenId = () => {
    try {
        return userInfoStore.userInfo.bindOpenId || false
    } catch (e) {
        return null;
    }
}

let pendingOnLaunchResolve = null;
let _onLaunchFinished = null;

export const onLaunchFinished = () => {
    _onLaunchFinished = true;
    pendingOnLaunchResolve && pendingOnLaunchResolve();
}

/**
 * 等待 onLaunch 运行结束
 */
export const pendingOnLaunch = () => {
    if (_onLaunchFinished) return;
    return new Promise(resolve => {
        pendingOnLaunchResolve = resolve;
        setTimeout(() => {
            resolve()
        }, 2000);
    })
}
